import * as React from 'react';
import {
    ReleaseStepBlockingConfig,
    ReleaseStepStatus,
    ReleaseStepType,
    ReleaseType,
    ReleaseTypesForNonBlockingSteps,
    WorkflowEnabledSteps,
} from '@/types';
import {
    getReleaseTypeForNonBlockingSteps,
    isHalfFilledStep,
    isInnerFilledStep,
    isNonBlockingStepForReleaseType,
    statusColorForReleaseStepStatus,
} from '@/helpers';
import Text from 'antd/lib/typography/Text';
import Popover from 'antd/lib/popover';
import List from 'antd/lib/list';
import Alert from 'antd/lib/alert/Alert';
import Space from 'antd/lib/space';
import { WorkflowRunStepRunGraph } from '@/types/workflowRunStepRunGraph';
import {
    antGold,
    antGrey,
    antRed,
    appHomeCardsWidgetTitleStyle,
    colorWithAlpha,
    runwayColors,
    rwColors,
} from '@/components/styles/globalStyles';
import { displayTextForReleaseStepStatus, displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { StatusNodeDot } from '@/components/StatusNode';
import Divider from 'antd/lib/divider';
import SmallStatusNode from '@/components/SmallStatusNode';
import {
    CheckCircleFilled,
    ClockCircleOutlined,
    ClockCircleFilled,
    CloseCircleFilled,
    ArrowRightOutlined,
} from '@ant-design/icons';
import { groupBy, keys, sortBy } from 'lodash';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import { createReleaseStepRoute, createStepRunRoute, ROUTER_REDIRECT } from '@/helpers/routeHelpers';
import { Link, useHistory } from 'react-router-dom';
import RightArrowCircledIcon from '@/components/icons/RightArrowCircledIcon';
import Button from 'antd/lib/button';
import { ArchetypeGroupSummaryDetails } from '@/types/workflowOverview';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectIsWorkflowsCIStepEnabled } from '@/selectors/selectors';

export interface WorkflowRunStepsGraphWidgetProps {
    runGraph: WorkflowRunStepRunGraph;
    workflowEnabledStepArchetypes: WorkflowEnabledSteps;
    archetypeGroupDetailsMap: Record<string, ArchetypeGroupSummaryDetails>;
    stepRunIdToDestinationStepConfigIdsMap: Record<string, string[]>;
    nonBlockingStepsConfigMap: Record<
        ReleaseTypesForNonBlockingSteps,
        Record<ReleaseStepType, ReleaseStepBlockingConfig>
    >;
    shouldHideNonBlockingArchetypeGroups: boolean;
    allDestinations: Destination[];
    selectedDestinationStepIds: string[];
    releaseType: ReleaseType;
    orgIdParam: string;
    appIdParam: string;
    releaseIdParam: string;
}

export const WorkflowRunStepsGraphWidget: React.FC<WorkflowRunStepsGraphWidgetProps> = ({
    runGraph,
    workflowEnabledStepArchetypes,
    archetypeGroupDetailsMap,
    stepRunIdToDestinationStepConfigIdsMap,
    nonBlockingStepsConfigMap,
    shouldHideNonBlockingArchetypeGroups,
    allDestinations,
    selectedDestinationStepIds,
    releaseType,
    orgIdParam,
    appIdParam,
    releaseIdParam,
}) => {
    const stepGroups = runGraph ? runGraph.stepGroups : [];

    const allArchetypeGroupsCount = stepGroups.reduce((count, current) => {
        return count + current.stepArchetypeGroups.length;
    }, 0);
    const normalWidgetWidth = 256;
    const nodeSize = 12;

    const gap = (normalWidgetWidth - allArchetypeGroupsCount * nodeSize) / allArchetypeGroupsCount;

    const history = useHistory();

    const selectedDestinations = React.useMemo(() => {
        return (
            allDestinations?.filter((dest) => {
                return selectedDestinationStepIds.includes(dest.stepConfigId);
            }) ?? []
        );
    }, [allDestinations, selectedDestinationStepIds]);

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, appIdParam),
    );

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}
        >
            <Text style={{ ...appHomeCardsWidgetTitleStyle }}>Release step status</Text>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                {stepGroups.map((stepGroup, i) => {
                    let filteredArchetypeGroupsInStepGroups = stepGroup.stepArchetypeGroups;
                    if (shouldHideNonBlockingArchetypeGroups) {
                        filteredArchetypeGroupsInStepGroups = stepGroup.stepArchetypeGroups.filter(
                            (archetypeGroup) =>
                                !isNonBlockingStepForReleaseType(
                                    nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(releaseType)],
                                    archetypeGroup.archetype,
                                ),
                        );
                    }

                    if (filteredArchetypeGroupsInStepGroups.length === 0) {
                        return null;
                    }

                    return (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap,
                                position: 'relative',
                            }}
                        >
                            {filteredArchetypeGroupsInStepGroups.map((archetypeGroup, archGroupIndex) => {
                                const stepRunStatusData =
                                    archetypeGroup.stepRunStatusDatum ? archetypeGroup.stepRunStatusDatum : [];

                                const archGroupSummaryInfos = archetypeGroupDetailsMap[archetypeGroup.id];

                                let outerBorderColor = antGrey.grey8;
                                let archGroupStatusNodeColor = statusColorForWidgetArchetypeGroupNode(
                                    archetypeGroup.combinedStatus,
                                );

                                let isArchGroupNodeInnerFilled = stepRunStatusData.some((stepRunStatusDatum) =>
                                    isInnerFilledStep(stepRunStatusDatum),
                                );
                                let isArchGroupNodeHalfFilled = stepRunStatusData.some((stepRunStatusDatum) =>
                                    isHalfFilledStep(stepRunStatusDatum),
                                );

                                const isNonBlockingArchetype = isNonBlockingStepForReleaseType(
                                    nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(releaseType)],
                                    archetypeGroup.archetype,
                                );

                                if (isNonBlockingArchetype) {
                                    archGroupStatusNodeColor = antGrey.grey6;
                                    outerBorderColor = antGrey.grey6;
                                    isArchGroupNodeInnerFilled = true;
                                }

                                const isWorkflowsMigratedAndEnabledArchetype =
                                    workflowEnabledStepArchetypes[archetypeGroup.archetype] === true;

                                let archGroupPath: string;
                                if (isWorkflowsMigratedAndEnabledArchetype) {
                                    // if step archetype is migrated / enabled, link to the first step run in the arch group
                                    // TODO-WF-OVERVIEW / TODO-WF-STEP-SUMMARY: link to the summary view instead, once available
                                    archGroupPath = createStepRunRoute(
                                        orgIdParam,
                                        appIdParam,
                                        releaseIdParam,
                                        archGroupSummaryInfos.archetypeGroupStepRunIds[0],
                                    );
                                } else {
                                    // otherwise link to the legacy release step
                                    archGroupPath = createReleaseStepRoute(
                                        orgIdParam,
                                        appIdParam,
                                        releaseIdParam,
                                        archetypeGroup.archetype,
                                    );
                                }

                                const statusNodeDataSource: ArchetypeGroupNodeStepRunDatum[] = stepRunStatusData.map(
                                    (statusDatum) => {
                                        const stepRunDestinationStepConfigIds =
                                            stepRunIdToDestinationStepConfigIdsMap[statusDatum.stepRunId];

                                        // collect a slice of the apis for the steprun's inferred destination steps;
                                        // this way we can render them alongside the steprun in the tooltip
                                        const matchingDestinations =
                                            isWorkflowsMigratedAndEnabledArchetype ?
                                                selectedDestinations.filter((destination) => {
                                                    return stepRunDestinationStepConfigIds.includes(
                                                        destination.stepConfigId,
                                                    );
                                                })
                                            :   selectedDestinations;

                                        // If we have a pending CLIs only, substitute a generic status string to facilitate grouping
                                        // stepruns by status string in the tooltip; otherwise use the status string as-is
                                        let statusString: string;
                                        if (statusDatum.status === ReleaseStepStatus.PendingChecklistOnly) {
                                            statusString =
                                                'One or more checklist items still need to be completed for this step.';
                                        } else {
                                            statusString = statusDatum.statusReasonString;
                                        }

                                        return {
                                            stepConfigId: statusDatum.stepConfigId,
                                            stepRunId: statusDatum.stepRunId,
                                            name: statusDatum.name,
                                            status: statusDatum.status,
                                            statusReasonString: statusString,
                                            matchingDestinations: matchingDestinations,
                                            orgIdParam: orgIdParam,
                                            appIdParam: appIdParam,
                                            releaseIdParam: releaseIdParam,
                                        };
                                    },
                                );

                                return (
                                    <div
                                        key={`archetypeGroup=${archetypeGroup.archetype}&index=${archGroupIndex}`}
                                        style={{
                                            position: 'relative',
                                            zIndex: 1,
                                        }}
                                    >
                                        <Popover
                                            content={
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'start',
                                                        gap: 12,
                                                        width: 350,
                                                        padding: '2px 4px 4px 4px',
                                                        height: '100%',
                                                    }}
                                                >
                                                    <Button
                                                        type="link"
                                                        style={{
                                                            fontWeight: 600,
                                                            padding: 0,
                                                            height: '22px',
                                                            alignSelf: 'start',
                                                        }}
                                                        onClick={() => {
                                                            history.push(archGroupPath, {
                                                                [ROUTER_REDIRECT]: true,
                                                            });
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'inline-flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                height: '22px',
                                                                gap: 10,
                                                            }}
                                                        >
                                                            {displayTextForReleaseStepType(
                                                                archetypeGroup.archetype,
                                                                null,
                                                                null,
                                                                isWorkflowsCIStepEnabled,
                                                            )}
                                                            <RightArrowCircledIcon />
                                                        </div>
                                                    </Button>
                                                    <GraphWidgetStatusNodeContent
                                                        statusNodeData={statusNodeDataSource}
                                                        nodeArchetype={archetypeGroup.archetype}
                                                        isNonBlockingArchGroup={isNonBlockingArchetype}
                                                        isArchetypeMigratedAndEnabled={
                                                            isWorkflowsMigratedAndEnabledArchetype
                                                        }
                                                    />
                                                </div>
                                            }
                                        >
                                            <div>
                                                <StatusNodeDot
                                                    statusColor={archGroupStatusNodeColor}
                                                    innerFilled={isArchGroupNodeInnerFilled}
                                                    halfFilled={isArchGroupNodeHalfFilled}
                                                    outerBorderColor={outerBorderColor}
                                                    size={nodeSize}
                                                    outerBorderWidth={1}
                                                />
                                            </div>
                                        </Popover>
                                    </div>
                                );
                            })}
                            <div
                                style={{
                                    position: 'absolute',
                                    height: 1,
                                    width: '100%',
                                    left: 0,
                                    zIndex: 0,
                                    top: 'calc(50% - 0.5px)',
                                    backgroundColor: antGrey.grey6,
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

interface GraphWidgetStatusNodeContentProps {
    statusNodeData: ArchetypeGroupNodeStepRunDatum[];
    nodeArchetype: ReleaseStepType;
    isNonBlockingArchGroup: boolean;
    isArchetypeMigratedAndEnabled: boolean;
}

interface ArchetypeGroupNodeStepRunDatum {
    stepConfigId: string;
    stepRunId: string;
    name: string;
    status: ReleaseStepStatus;
    statusReasonString: string;
    matchingDestinations: Destination[];
    orgIdParam?: string;
    appIdParam?: string;
    releaseIdParam?: string;
}

const stepStatusOrderIndices: Record<ReleaseStepStatus, number> = {
    blocked: 0,
    ready: 1,
    pendingChecklist: 3,
    pending: 4,
    inactive: 5,
};

const GraphWidgetStatusNodeContent: React.FC<GraphWidgetStatusNodeContentProps> = ({
    statusNodeData,
    nodeArchetype,
    isNonBlockingArchGroup,
    isArchetypeMigratedAndEnabled,
}) => {
    // group step run infos that share a status and reason string together;
    // this way we can render them in a block under a common status string component
    const dataGroupedByStatusAndReason = groupBy(statusNodeData, (statusNodeDatum) => {
        return `${statusNodeDatum.status}&reasonString=${statusNodeDatum.statusReasonString}`;
    });

    const groupKeys = keys(dataGroupedByStatusAndReason);
    const sortedKeys = sortBy(groupKeys, (groupKey) => {
        const groupStatusData: ArchetypeGroupNodeStepRunDatum[] = dataGroupedByStatusAndReason[groupKey];

        const groupStatus = groupStatusData[0].status;
        return stepStatusOrderIndices[groupStatus];
    });

    return (
        <List
            style={{
                maxHeight: '330px',
                overflowY: 'scroll',
                width: '100%',
            }}
            dataSource={sortedKeys}
            locale={{ emptyText: 'No matching step runs.' }}
            split={false}
            renderItem={(groupKey, index) => {
                const groupStatusDataSource: ArchetypeGroupNodeStepRunDatum[] = dataGroupedByStatusAndReason[groupKey];

                const groupStatus = groupStatusDataSource[0].status;
                const groupStatusReason = groupStatusDataSource[0].statusReasonString;

                const isLast = index === sortedKeys.length - 1;
                const shouldRenderDivider = sortedKeys.length > 0 && !isLast;

                let statusNodeColor = statusColorForReleaseStepStatus(groupStatus);
                const statusString = groupStatusReason ? groupStatusReason : 'Awaiting step status.';

                return (
                    <>
                        <List.Item
                            style={{
                                padding: '2px 0px 0px 4px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'start',
                                gap: '10px',
                            }}
                        >
                            <StepRunAlertComponent
                                status={groupStatus}
                                statusString={statusString}
                                isNonBlocking={isNonBlockingArchGroup}
                            />
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    width: '100%',
                                    padding: 0,
                                }}
                            >
                                {groupStatusDataSource.map((statusNodeDatum, i) => {
                                    const isLast = i === groupStatusDataSource.length - 1;
                                    const shouldAddMargin = !isLast && groupStatusDataSource.length > 1;

                                    let stepRunOrLegacyStepPath: string;
                                    if (
                                        !!statusNodeDatum.orgIdParam &&
                                        !!statusNodeDatum.appIdParam &&
                                        !!statusNodeDatum.releaseIdParam
                                    ) {
                                        if (isArchetypeMigratedAndEnabled) {
                                            // if step archetype is migrated / enabled, link to the step run
                                            stepRunOrLegacyStepPath = createStepRunRoute(
                                                statusNodeDatum.orgIdParam,
                                                statusNodeDatum.appIdParam,
                                                statusNodeDatum.releaseIdParam,
                                                statusNodeDatum.stepRunId,
                                            );
                                        } else {
                                            // otherwise link to the legacy release step
                                            stepRunOrLegacyStepPath = createReleaseStepRoute(
                                                statusNodeDatum.orgIdParam,
                                                statusNodeDatum.appIdParam,
                                                statusNodeDatum.releaseIdParam,
                                                nodeArchetype,
                                            );
                                        }
                                    }

                                    return (
                                        <li
                                            style={{
                                                height: '26px',
                                                width: '100%',
                                                margin: shouldAddMargin ? '4px 4px 14px 0px' : '4px 4px 4px 0px',
                                            }}
                                            key={`stepConfigId=${statusNodeDatum.stepConfigId}&i=${i}`}
                                        >
                                            <div
                                                style={{
                                                    height: '26px',
                                                    width: '100%',
                                                    paddingRight: '4px',
                                                    display: 'inline-flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Space
                                                    direction="horizontal"
                                                    align="center"
                                                    size={12}
                                                    style={{
                                                        height: '26px',
                                                    }}
                                                >
                                                    <SmallStatusNode color={statusNodeColor} size={8} />
                                                    <DestinationCounter
                                                        destinations={statusNodeDatum.matchingDestinations}
                                                        size={26}
                                                    />
                                                    <Text
                                                        style={{
                                                            fontFamily: 'Inter',
                                                            color: antGrey.grey8,
                                                            fontWeight: 200,
                                                            height: '26px',
                                                            maxWidth: '225px',
                                                            lineHeight: '26px',
                                                        }}
                                                        ellipsis
                                                    >
                                                        {statusNodeDatum.name}
                                                    </Text>
                                                </Space>
                                                {!!stepRunOrLegacyStepPath && (
                                                    <Link
                                                        to={stepRunOrLegacyStepPath}
                                                        style={{
                                                            height: '26px',
                                                        }}
                                                    >
                                                        <ArrowRightOutlined
                                                            style={{
                                                                fontSize: 15,
                                                                verticalAlign: 'middle',
                                                                color: runwayColors.primary,
                                                            }}
                                                        />
                                                    </Link>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </List.Item>
                        {shouldRenderDivider && (
                            <div
                                style={{
                                    paddingLeft: '4px',
                                }}
                            >
                                <Divider
                                    style={{
                                        marginTop: '11px',
                                        marginBottom: '13px',
                                    }}
                                />
                            </div>
                        )}
                    </>
                );
            }}
        />
    );
};

interface StepRunAlertComponentProps {
    status: ReleaseStepStatus;
    statusString: string;
    isNonBlocking: boolean;
}

const StepRunAlertComponent: React.FC<StepRunAlertComponentProps> = ({ status, statusString, isNonBlocking }) => {
    const alertBGColor = stepRunStatusBGColor(status, isNonBlocking);
    const alertBorderColor = stepRunStatusOutlineColor(status, isNonBlocking);

    return (
        <Alert
            showIcon={false}
            message={<StepRunAlertContent status={status} statusString={statusString} isNonBlocking={isNonBlocking} />}
            style={{
                backgroundColor: alertBGColor,
                borderColor: alertBorderColor,
                width: '100%',
                borderRadius: '8px',
                padding: '9px 16px 9px 16px',
            }}
        />
    );
};

interface StepRunAlertContentProps {
    status: ReleaseStepStatus;
    statusString: string;
    isNonBlocking: boolean;
}

const StepRunAlertContent: React.FC<StepRunAlertContentProps> = ({ status, statusString, isNonBlocking }) => {
    const statusColor = stepRunStatusColor(status, isNonBlocking);
    const alertIcon = stepRunStatusIcon(status, statusColor);
    const alertStatusTitle = displayTextForReleaseStepStatus(status);

    return (
        <div
            style={{
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'start',
                gap: 10,
            }}
        >
            <span>{alertIcon}</span>
            <span>
                <span
                    style={{
                        color: statusColor,
                        fontWeight: '600',
                        paddingRight: 8,
                    }}
                >
                    {alertStatusTitle}
                </span>
                <span
                    style={{
                        color: statusColor,
                        fontWeight: '500',
                        opacity: '0.5',
                        paddingRight: 8,
                    }}
                >
                    /
                </span>
                <span
                    style={{
                        color: antGrey.grey8,
                        fontWeight: 100,
                    }}
                >
                    {statusString}
                </span>
            </span>
        </div>
    );
};

const statusColorForWidgetArchetypeGroupNode = (combinedStatus: ReleaseStepStatus) => {
    if (!combinedStatus) {
        return runwayColors.lightBackgroundGrey;
    }

    return statusColorForReleaseStepStatus(combinedStatus);
};

const stepRunStatusColor = (status: ReleaseStepStatus, isNonBlocking: boolean) => {
    return isNonBlocking ? runwayColors.statusGrey : statusColorForReleaseStepStatus(status);
};

const stepRunStatusBGColor = (status: ReleaseStepStatus, isNonBlocking: boolean) => {
    if (isNonBlocking) return antGrey.grey2;

    switch (status) {
        case ReleaseStepStatus.Ready:
            return colorWithAlpha(rwColors.statusGreen, 0.1);
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return antGold.gold1;
        case ReleaseStepStatus.Blocked:
            return antRed.red1;
        case ReleaseStepStatus.Inactive:
            return antGrey.grey2;
    }
};

const stepRunStatusOutlineColor = (status: ReleaseStepStatus, isNonBlocking: boolean) => {
    if (isNonBlocking) return antGrey.grey5;

    switch (status) {
        case ReleaseStepStatus.Ready:
            return colorWithAlpha(rwColors.statusGreen, 0.5);
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return antGold.gold4;
        case ReleaseStepStatus.Blocked:
            return antRed.red4;
        case ReleaseStepStatus.Inactive:
            return antGrey.grey5;
    }
};

const stepRunStatusIcon = (status: ReleaseStepStatus, iconColor: string) => {
    switch (status) {
        case ReleaseStepStatus.Ready:
            return <CheckCircleFilled style={{ color: iconColor ?? runwayColors.statusGreen }} />;
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return <ClockCircleFilled style={{ color: iconColor ?? runwayColors.statusYellow }} />;
        case ReleaseStepStatus.Blocked:
            return <CloseCircleFilled style={{ color: iconColor ?? runwayColors.statusRed }} />;
        case ReleaseStepStatus.Inactive:
            return <ClockCircleOutlined style={{ color: iconColor ?? runwayColors.statusGrey }} />;
    }
};
